/**
 * TemplateOfferPrompt — the opt-in "Create templates from these items?" modal.
 *
 * Shared by the Item Catalog (items.html, after the Add Items experience saves
 * a batch) and the Invoice Inbox (after an emailed invoice approved with "Add
 * Items Only" discovers new items).
 *
 * This modal is the OPT-IN only. Saying yes opens CounterIntelligence AI —
 * exactly what a brand-new store gets at the end of setup — instead of running
 * a second, silent template builder here. The assistant proposes the same
 * default (one end-of-period count with everything, plus one ordering template
 * per category), shows it as graphical template cards, lets the user talk
 * through changes, and creates the templates when they approve. Existing
 * matching templates are appended to rather than duplicated, there too.
 *
 * The assistant's overlay lives on the Count page, so "yes" stashes the batch
 * and navigates there (utils/templateSetupLaunch). Nothing is created here.
 *
 * Props:
 *   itemIds   — inventory_items IDs of the just-saved batch (required)
 *   onClose   — called when the modal is dismissed
 *   storeId   — optional; falls back to localStorage currentStore
 *   introText — optional override for the ask-step lead sentence
 *   helperText — optional override for the ask-step supporting sentence
 *   title, dismissLabel, confirmLabel — optional ask-step copy overrides
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { launchTemplateSetup } from '../utils/templateSetupLaunch';

const TemplateOfferPrompt = ({
  itemIds,
  onClose,
  storeId,
  introText,
  helperText,
  title = 'Create templates from these items?',
  dismissLabel = 'Not now',
  confirmLabel = 'Yes, create templates',
}) => {
  const ids = Array.from(new Set((itemIds || []).filter(Boolean)));
  const [state, setState] = useState({ status: 'ask', error: null });
  const dialogRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    dialogRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (state.status !== 'opening' && typeof onClose === 'function') onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ));
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, state.status]);

  if (ids.length === 0) return null;

  const dismiss = () => { if (typeof onClose === 'function') onClose(); };

  const resolveStoreId = () => {
    if (storeId) return storeId;
    try { return JSON.parse(localStorage.getItem('currentStore') || '{}')?.id || null; } catch (_) { return null; }
  };

  // Yes → hand the batch to CounterIntelligence AI on the Count page. The
  // navigation is the last thing that happens; a failed stash keeps the user
  // here with a message rather than dropping them on a page with no handoff.
  const openCounterIntelligence = () => {
    if (state.status === 'opening') return;
    const sid = resolveStoreId();
    if (!sid) {
      setState({ status: 'error', error: 'Missing store context. Please refresh and try again.' });
      return;
    }
    setState({ status: 'opening', error: null });
    const launched = launchTemplateSetup({ storeId: sid, itemIds: ids, source: 'add_items' });
    if (!launched) {
      setState({
        status: 'error',
        error: 'Your items are saved, but this browser wouldn’t let us open the template review. Open Counts and ask CounterIntelligence AI to organize your new items.',
      });
    }
  };

  const isOpening = state.status === 'opening';

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2200,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-offer-title"
        tabIndex={-1}
        style={{
          background: '#fff', borderRadius: '12px', padding: '1.5rem',
          maxWidth: '460px', width: '90%', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)', outline: 'none'
        }}
      >
        <h5 id="template-offer-title" style={{ marginBottom: '0.75rem', color: '#3a2e28' }}>{title}</h5>
        <p style={{ fontSize: '0.9rem', color: '#5A4A42', marginBottom: '0.75rem' }}>
          {introText || (
            <>
              Countertop-App can turn the{' '}
              <strong>{ids.length}</strong> item{ids.length === 1 ? '' : 's'}{' '}
              you just imported into ready-to-use templates — the same way it does during setup.
            </>
          )}
        </p>
        <p style={{ fontSize: '0.85rem', color: '#6B5A52', marginBottom: state.status === 'error' ? '0.75rem' : '1.25rem' }}>
          {helperText || (
            <>
              <strong>CounterIntelligence AI</strong> will suggest one <strong>end-of-period</strong> count template
              with these items plus an <strong>ordering template for each category</strong>, and talk through any
              changes with you before it creates anything.
            </>
          )}
        </p>
        {state.status === 'error' && (
          <div className="alert alert-danger py-2" style={{ fontSize: '0.82rem', marginBottom: '1rem' }}>
            {state.error}
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={dismiss}
            disabled={isOpening}
          >
            {dismissLabel}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={openCounterIntelligence}
            disabled={isOpening}
          >
            {isOpening ? 'Opening…' : state.status === 'error' ? 'Try again' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TemplateOfferPrompt;
